import { parseSentrySdkConfig, type SentrySdkConfig } from "../config";

let initialized = false;
let sentrySdk: FrontendSentrySdk | null = null;
let sentrySdkImportPromise: Promise<FrontendSentrySdk> | null = null;
let initializationPromise: Promise<boolean> | null = null;
const sentryEventIdsByException = new WeakMap<object, string>();
const sentryEventIdsBySignature = new Map<string, string>();
const BACKSLASH = String.fromCodePoint(92);
const REGEX_SPECIAL_CHARS = new Set([
  ".",
  "*",
  "+",
  "?",
  "^",
  "$",
  "{",
  "}",
  "(",
  ")",
  "|",
  "[",
  "]",
  BACKSLASH,
]);
type FrontendSentryInitOptions = Readonly<{
  tracePropagationTargets?: Array<string | RegExp>;
  routePatterns?: readonly string[];
}>;

type BrowserTracingOptions = Readonly<{
  beforeStartSpan: <T extends { name: string }>(context: T) => T;
}>;

type FrontendSentryEvent = Readonly<{
  event_id?: string;
}>;

type FrontendSentryHint = Readonly<{
  originalException?: unknown;
}>;

type FrontendSentryIntegration = Readonly<{
  name: string;
}>;

type FrontendSentrySdkSpan = Readonly<{
  end: () => void;
  setAttribute: (key: string, value: string | number | boolean) => void;
}>;

type FrontendSentrySdk = Readonly<{
  init: (options: {
    dsn?: string;
    environment?: string;
    release?: string;
    tracesSampleRate: number;
    debug: boolean;
    sendDefaultPii: boolean;
    tracePropagationTargets?: Array<string | RegExp>;
    beforeSend: (
      event: FrontendSentryEvent,
      hint: FrontendSentryHint,
    ) => FrontendSentryEvent | null;
    integrations: (
      defaultIntegrations: FrontendSentryIntegration[],
    ) => FrontendSentryIntegration[];
  }) => void;
  browserTracingIntegration: (
    options: BrowserTracingOptions,
  ) => FrontendSentryIntegration;
  captureException: (
    error: unknown,
    options?: { event_id?: string },
  ) => string | undefined;
  lastEventId: () => string | undefined;
  flush: (timeoutMs?: number) => Promise<boolean>;
  captureFeedback: (feedback: FrontendSentryFeedback) => string | undefined;
  getTraceData: () => Record<string, unknown>;
  startInactiveSpan: (options: {
    name: string;
    op: string;
    attributes: Record<string, string | number | boolean>;
  }) => FrontendSentrySdkSpan;
  withActiveSpan: <T>(span: FrontendSentrySdkSpan, cb: () => T) => T;
}>;

type RouteMatcher = Readonly<{
  pattern: string;
  regex: RegExp;
  staticSegmentCount: number;
  segmentCount: number;
}>;

export type FrontendSentrySpan = Readonly<{
  end: () => void;
  setData: (key: string, data: unknown) => void;
  withActive: <T>(cb: () => T) => T;
}>;

export type FrontendSentrySpanOptions = Readonly<{
  op?: string;
  attributes?: Record<string, unknown>;
}>;

export type FrontendSentryFeedback = Readonly<{
  message: string;
  name?: string;
  email?: string;
  associatedEventId?: string;
  url?: string;
}>;

export type FrontendSentryExceptionOptions = Readonly<{
  eventId?: string;
}>;

export function getFrontendSentryConfig(
  config: Record<string, string | undefined>,
): SentrySdkConfig {
  return parseSentrySdkConfig(config);
}

export function initFrontendSentry(
  config: SentrySdkConfig,
  options: FrontendSentryInitOptions = {},
): Promise<boolean> {
  if (!config.enabled || initialized) return Promise.resolve(initialized);
  // Memoize the in-flight init so concurrent callers (e.g. React StrictMode
  // double-mounts or multiple bootstrap paths) share one initialization and
  // `sdk.init` only runs once.
  initializationPromise ??= doInitFrontendSentry(config, options).catch(
    (error) => {
      initializationPromise = null;
      throw error;
    },
  );
  return initializationPromise;
}

async function doInitFrontendSentry(
  config: SentrySdkConfig,
  options: FrontendSentryInitOptions,
): Promise<boolean> {
  const sdk = await loadFrontendSentrySdk();
  const browserTracingOptions = createBrowserTracingOptions(
    options.routePatterns,
  );

  sdk.init({
    dsn: config.dsn,
    environment: config.environment,
    release: config.release,
    tracesSampleRate: config.tracesSampleRate,
    debug: config.debug,
    sendDefaultPii: config.sendDefaultPii,
    tracePropagationTargets: options.tracePropagationTargets,
    beforeSend: (event, hint) => {
      if (isBackendMirroredGraphQLError(hint.originalException)) {
        return null;
      }
      rememberFrontendSentryEvent(event.event_id, hint.originalException);
      return event;
    },
    integrations: (defaultIntegrations) => [
      ...defaultIntegrations.filter(
        (integration) =>
          config.autoSessionTracking || integration.name !== "BrowserSession",
      ),
      sdk.browserTracingIntegration(browserTracingOptions),
    ],
  });
  initialized = true;
  return true;
}

export function normalizeFrontendSentryRouteName(
  name: string,
  routePatterns: readonly string[] | undefined,
): string {
  const pathname = getPathnameFromTransactionName(name);
  if (!pathname || !routePatterns || routePatterns.length === 0) return name;

  const match = createRouteMatchers(routePatterns).find((matcher) =>
    matcher.regex.test(pathname),
  );
  return match?.pattern ?? name;
}

export function isFrontendSentryEnabled(): boolean {
  return initialized;
}

export function createFrontendSentryEventId(): string | undefined {
  const browserCrypto = globalThis.crypto;
  if (typeof browserCrypto?.randomUUID === "function") {
    return browserCrypto.randomUUID().replaceAll("-", "");
  }

  if (typeof browserCrypto?.getRandomValues !== "function") {
    return undefined;
  }

  const bytes = new Uint8Array(16);
  browserCrypto.getRandomValues(bytes);
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join(
    "",
  );
}

export function captureFrontendException(
  error: unknown,
  options: FrontendSentryExceptionOptions = {},
): string | undefined {
  const sdk = sentrySdk;
  if (!initialized || !sdk) return undefined;
  // `beforeSend` drops these: the backend already reported the failure. Its
  // event id is the only one that exists, so no id is better than a made-up one.
  if (isBackendMirroredGraphQLError(error)) {
    return getBackendSentryEventId(error);
  }
  const existingEventId = getFrontendSentryEventIdForException(error);
  if (existingEventId) return existingEventId;
  const signature = getFrontendSentryExceptionSignature(error);
  const existingSignatureEventId =
    getFrontendSentryEventIdForSignature(signature);
  if (existingSignatureEventId) return existingSignatureEventId;

  const eventId = options.eventId;
  const capturedEventId = eventId
    ? sdk.captureException(error, { event_id: eventId })
    : sdk.captureException(error);
  return (
    getFrontendSentryEventIdForException(error) ??
    getFrontendSentryEventIdForSignature(signature) ??
    capturedEventId
  );
}

export function getLastFrontendSentryEventId(): string | undefined {
  const sdk = sentrySdk;
  if (!initialized || !sdk) return undefined;
  return sdk.lastEventId();
}

export function getFrontendSentryEventIdForException(
  exception: unknown,
): string | undefined {
  if (!initialized || !isWeakMapKey(exception)) return undefined;
  return sentryEventIdsByException.get(exception);
}

export async function flushFrontendSentry(timeoutMs = 2_000): Promise<boolean> {
  const sdk = sentrySdk;
  if (!initialized || !sdk) return true;
  return sdk.flush(timeoutMs);
}

export function captureFrontendFeedback(
  feedback: FrontendSentryFeedback,
): string | undefined {
  const sdk = sentrySdk;
  if (!initialized || !sdk) return undefined;
  return sdk.captureFeedback({
    ...feedback,
    message: feedback.message.trim(),
  });
}

export function getSentryTraceHeaders(): Record<string, string> {
  const sdk = sentrySdk;
  if (!initialized || !sdk) return {};

  const traceData = sdk.getTraceData();
  return Object.fromEntries(
    Object.entries(traceData).filter(
      (entry): entry is [string, string] => typeof entry[1] === "string",
    ),
  );
}

export function withFrontendSentrySpan<T>(
  name: string,
  options: FrontendSentrySpanOptions,
  cb: (span: FrontendSentrySpan) => T,
): T {
  const sdk = sentrySdk;
  if (!initialized || !sdk) return cb(new NoopFrontendSentrySpan());

  const sentrySpan = sdk.startInactiveSpan({
    name,
    op: options.op ?? "ui.action",
    attributes: sanitizeSpanAttributes(options.attributes ?? {}),
  });
  return sdk.withActiveSpan(sentrySpan, () =>
    cb(makeFrontendSpanAdapter(sentrySpan, sdk)),
  );
}

async function loadFrontendSentrySdk(): Promise<FrontendSentrySdk> {
  sentrySdkImportPromise ??= import(
    "@sentry/react"
  ) as unknown as Promise<FrontendSentrySdk>;
  sentrySdk = await sentrySdkImportPromise;
  return sentrySdk;
}

function rememberFrontendSentryEvent(
  eventId: string | undefined,
  exception: unknown,
): void {
  if (!eventId) return;
  const signature = getFrontendSentryExceptionSignature(exception);
  if (signature) {
    sentryEventIdsBySignature.set(signature, eventId);
  }
  if (isWeakMapKey(exception)) {
    sentryEventIdsByException.set(exception, eventId);
  }
}

function getFrontendSentryEventIdForSignature(
  signature: string | undefined,
): string | undefined {
  return signature ? sentryEventIdsBySignature.get(signature) : undefined;
}

function getFrontendSentryExceptionSignature(
  exception: unknown,
): string | undefined {
  if (exception instanceof Error) {
    return [exception.name, exception.message, exception.stack ?? ""].join(
      "\n",
    );
  }
  return undefined;
}

function isWeakMapKey(value: unknown): value is object {
  return (
    (typeof value === "object" && value !== null) || typeof value === "function"
  );
}

class NoopFrontendSentrySpan implements FrontendSentrySpan {
  public end = (): void => undefined;
  public setData = (_key: string, _data: unknown): void => undefined;
  public withActive = <T>(cb: () => T): T => cb();
}

function makeFrontendSpanAdapter(
  span: FrontendSentrySdkSpan,
  sdk: FrontendSentrySdk,
): FrontendSentrySpan {
  let ended = false;

  return {
    end: () => {
      if (ended) return;
      ended = true;
      span.end();
    },
    setData: (key: string, data: unknown) => {
      const value = sanitizeSpanValue(data);
      if (value !== undefined) {
        span.setAttribute(key, value);
      }
    },
    withActive: <T>(cb: () => T): T => sdk.withActiveSpan(span, cb),
  };
}

function sanitizeSpanAttributes(
  attributes: Record<string, unknown>,
): Record<string, string | number | boolean> {
  return Object.fromEntries(
    Object.entries(attributes).flatMap(([key, value]) => {
      const sanitized = sanitizeSpanValue(value);
      return sanitized === undefined ? [] : [[key, sanitized]];
    }),
  );
}

function sanitizeSpanValue(
  value: unknown,
): string | number | boolean | undefined {
  if (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean" ||
    value === undefined
  ) {
    return value;
  }
  if (value === null) return undefined;

  try {
    return JSON.stringify(value);
  } catch {
    return undefined;
  }
}

function createBrowserTracingOptions(
  routePatterns: readonly string[] | undefined,
): BrowserTracingOptions {
  return {
    beforeStartSpan: (context) => ({
      ...context,
      name: normalizeFrontendSentryRouteName(context.name, routePatterns),
    }),
  };
}

function getPathnameFromTransactionName(name: string): string | undefined {
  if (name.startsWith("/")) {
    return normalizePathname(name);
  }

  return undefined;
}

function normalizePathname(pathname: string): string {
  const pathOnly = pathname.split(/[?#]/, 1)[0] ?? "/";
  if (pathOnly === "") return "/";
  return stripTrailingSlashes(pathOnly);
}

function normalizeRoutePattern(pattern: string): string {
  if (pattern === "") return "/";
  return stripTrailingSlashes(pattern);
}

function stripTrailingSlashes(value: string): string {
  if (value.length <= 1) return value;

  let endIndex = value.length;
  while (endIndex > 1 && value[endIndex - 1] === "/") {
    endIndex -= 1;
  }
  return value.slice(0, endIndex);
}

function createRouteMatchers(routePatterns: readonly string[]): RouteMatcher[] {
  return routePatterns
    .map(createRouteMatcher)
    .filter((matcher): matcher is RouteMatcher => matcher !== undefined)
    .sort((a, b) => {
      const staticDiff = b.staticSegmentCount - a.staticSegmentCount;
      if (staticDiff !== 0) return staticDiff;
      return b.segmentCount - a.segmentCount;
    });
}

function createRouteMatcher(pattern: string): RouteMatcher | undefined {
  const normalizedPattern = normalizeRoutePattern(pattern);
  if (!normalizedPattern.startsWith("/")) return undefined;

  const segments =
    normalizedPattern === "/" ? [] : normalizedPattern.slice(1).split("/");
  const staticSegmentCount = segments.filter(
    (segment) => !isDynamicSegment(segment),
  ).length;
  const regexParts = segments.map((segment) => {
    if (segment === "*") return "(?:/.*)?";
    if (isOptionalDynamicSegment(segment)) return "(?:/[^/]+)?";
    if (isDynamicSegment(segment)) return "/[^/]+";
    return `/${escapeRegex(segment)}`;
  });

  return {
    pattern: normalizedPattern,
    regex: new RegExp(`^${regexParts.join("")}/?$`),
    staticSegmentCount,
    segmentCount: segments.length,
  };
}

function isDynamicSegment(segment: string): boolean {
  return segment.startsWith(":");
}

function isOptionalDynamicSegment(segment: string): boolean {
  return segment.startsWith(":") && segment.endsWith("?");
}

function escapeRegex(value: string): string {
  return Array.from(value, (char) =>
    REGEX_SPECIAL_CHARS.has(char) ? `${BACKSLASH}${char}` : char,
  ).join("");
}

function isBackendMirroredGraphQLError(error: unknown): boolean {
  if (error instanceof Error && error.name === "GraphQLRequestError") {
    return true;
  }

  return isLegacyGraphQLErrorResponseObject(error);
}

/**
 * The event id the backend put on a GraphQL error: `sentryEventId` on a
 * `GraphQLRequestError`, `extensions.sentryEventId` on a raw response error.
 */
function getBackendSentryEventId(error: unknown): string | undefined {
  if (typeof error !== "object" || error === null) return undefined;
  const { sentryEventId, extensions } = error as {
    sentryEventId?: unknown;
    extensions?: { sentryEventId?: unknown } | null;
  };
  const eventId = sentryEventId ?? extensions?.sentryEventId;
  return typeof eventId === "string" && eventId.trim().length > 0
    ? eventId.trim()
    : undefined;
}

function isLegacyGraphQLErrorResponseObject(value: unknown): boolean {
  return (
    typeof value === "object" &&
    value !== null &&
    "message" in value &&
    typeof value.message === "string" &&
    ("extensions" in value || "locations" in value || "path" in value)
  );
}
